import React, { ChangeEventHandler, FocusEventHandler, Ref } from 'react';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  RegisterOptions,
  UseFormStateReturn,
} from 'react-hook-form';

import {
  CascaderProps,
  CheckboxGroupProps,
  CheckboxProps,
  DatePickerProps,
  RadioProps,
  RadioGroupProps,
  SelectProps,
} from '../../index';

export type RenderProps = {
  field: ControllerRenderProps<FieldValues, string>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<FieldValues>;
  rules?: RegisterOptions;
};

export interface TagOptionsType {
  id: string;
  name: string;
}

export interface TagRefProps {
  getValue: () => string;
  getInputValue: () => string;
}
export interface TagsInputProps {
  ref?: Ref<TagRefProps>;
  value?: string;
  variation?: 'error' | 'warning';
  disabled?: boolean;
  onChange?: (value?: string, result?: TagOptionsType[]) => void;
  onInput?: (value: string, setValue: React.Dispatch<React.SetStateAction<string>>) => void;
  maxLength?: number;
  placeholder?: string;
}

type InputRenderProps = {
  id?: string;
  value: string;
  variation?: 'error' | 'warning';
  forwardedRef: Ref<HTMLInputElement>;
  maxLength?: number;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLInputElement>;
};

type TextareaRenderProps = {
  id?: string;
  value: string;
  variation?: 'error' | 'warning';
  forwardedRef: Ref<HTMLTextAreaElement>;
  maxLength?: number;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur: FocusEventHandler<HTMLTextAreaElement>;
};

type SelectRenderProps = Pick<SelectProps, 'value' | 'variation' | 'forwardedRef' | 'onChange'>;

type CascaderRenderProps = Pick<CascaderProps, 'value' | 'variation' | 'forwardedRef' | 'onChange'>;

type CheckboxRenderProps = Pick<CheckboxProps, 'checked' | 'forwardedRef' | 'onChange'>;

type CheckboxGroupRenderProps = Pick<CheckboxGroupProps, 'value' | 'forwardedRef' | 'onChange'>;

type RadioRenderProps = Pick<RadioProps, 'checked' | 'forwardedRef' | 'onChange'>;

type RadioGroupRenderProps = Pick<RadioGroupProps, 'value' | 'forwardedRef' | 'onChange'>;

type DatePickerRenderProps = Pick<DatePickerProps, 'value' | 'variation' | 'onChange'>;

type RequesterRenderProps = Pick<SelectProps, 'value' | 'variation' | 'forwardedRef' | 'onChange'>;

type TagsInputRenderProps = Pick<TagsInputProps, 'value' | 'variation' | 'onChange'>;

export function convertInput(renderProps: RenderProps): InputRenderProps {
  const { field, fieldState, rules } = renderProps;

  return {
    id: field.name,
    value: field.value || '',
    variation: fieldState.error ? 'error' : undefined,
    maxLength: Number(rules?.maxLength) || undefined,
    forwardedRef: field.ref,
    onChange: field.onChange,
    onBlur: field.onBlur,
  };
}

export function convertTextarea(renderProps: RenderProps): TextareaRenderProps {
  const { field, fieldState, rules } = renderProps;

  return {
    id: field.name,
    value: field.value || '',
    variation: fieldState.error ? 'error' : undefined,
    maxLength: Number(rules?.maxLength) || undefined,
    forwardedRef: field.ref,
    onChange: field.onChange,
    onBlur: field.onBlur,
  };
}

export function convertSelect(renderProps: RenderProps): SelectRenderProps {
  const { field, fieldState } = renderProps;

  return {
    value: field.value,
    variation: fieldState.error ? 'error' : undefined,
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertCascader(renderProps: RenderProps): CascaderRenderProps {
  const { field, fieldState } = renderProps;

  return {
    value: field.value,
    variation: fieldState.error ? 'error' : undefined,
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertCheckbox(renderProps: RenderProps): CheckboxRenderProps {
  const { field } = renderProps;

  return {
    checked: field.value,
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertCheckboxGroup(renderProps: RenderProps): CheckboxGroupRenderProps {
  const { field } = renderProps;

  return {
    value: field.value || [],
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertRadio(renderProps: RenderProps): RadioRenderProps {
  const { field } = renderProps;

  return {
    checked: field.value,
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertRadioGroup(renderProps: RenderProps): RadioGroupRenderProps {
  const { field } = renderProps;

  return {
    value: field.value,
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertDatePicker(renderProps: RenderProps): DatePickerRenderProps {
  const { field, fieldState } = renderProps;

  return {
    value: field.value || '',
    variation: fieldState.error ? 'error' : undefined,
    onChange: field.onChange,
  };
}

export function convertRequester(renderProps: RenderProps): RequesterRenderProps {
  const { field, fieldState } = renderProps;

  return {
    value: field.value,
    variation: fieldState.error ? 'error' : undefined,
    forwardedRef: field.ref,
    onChange: field.onChange,
  };
}

export function convertTagsInput(renderProps: RenderProps): TagsInputRenderProps {
  const { field, fieldState } = renderProps;

  return {
    value: field.value,
    variation: fieldState.invalid ? 'error' : undefined,
    onChange: field.onChange,
  };
}
